import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Button, Spin, message, Alert } from "antd";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import { API } from "../../Config/API";
import { GET } from "../../utils/apiCalls";
import { useSubscriptionCheck } from "../../hooks/useSubscriptionCheck";
import { storePaymentData } from "../../store/paymentDetails";
import {
  DEFAULT_COUNTRY,
  ONLINE_PAYMENT_UNAVAILABLE_MESSAGE,
  findCountry,
  plansForCountry,
  resolvePlanPrice,
  useEpaperCountries,
} from "../../utils/epaperPricing";

type Plan = {
  id: number;
  plan_name: string;
  type: string;
  price: number | string;
  duration_type: "days" | "months" | "years";
  duration_value: number;
  region?: string | null;
  is_active: boolean;
  status?: string | null;
  sort_order?: number | null;
  allowed_role_ids?: number[];
  country_prices?: Record<string, { currency?: string; price: number | string }> | string | null;
};

function addPlanDuration(start: moment.Moment, plan: Plan): moment.Moment {
  const v = Number(plan.duration_value) || 0;
  if (!v) return start.clone();
  switch (plan.duration_type) {
    case "days":   return start.clone().add(v, "days");
    case "months": return start.clone().add(v, "months");
    case "years":  return start.clone().add(v, "years");
    default:       return start.clone();
  }
}

function durationLabel(plan: Plan): string {
  const v = Number(plan.duration_value) || 0;
  const t = plan.duration_type;
  if (!v) return "";
  return v === 1 ? `1 ${t.slice(0, -1)}` : `${v} ${t}`;
}

interface Props {
  visible: boolean;
  onClose: () => void;
}

function RenewalModal({ visible, onClose }: Props) {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const user = useSelector((state: any) => state.auth.user);
  const token = useSelector((state: any) => state.auth.token);
  const { status: subStatus, endDate } = useSubscriptionCheck();
  const isNew = subStatus === "none";
  const isExpired = subStatus === "expired";

  const modalTitle = isNew
    ? "Subscribe to ePaper"
    : isExpired
    ? "Renew Subscription"
    : "Renew or Upgrade Subscription";

  const introText = isNew
    ? "Choose a plan to start reading the ePaper."
    : isExpired
    ? "Your subscription has expired. Choose a plan to resume reading."
    : "Pick a plan to extend or upgrade your current subscription.";

  const submitLabel = isNew ? "Subscribe & Pay" : "Proceed to Payment";

  const [plans, setPlans] = useState<Plan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Set when the server reports that PayTabs can't charge this currency.
  const [onlineUnavailable, setOnlineUnavailable] = useState(false);

  // Plans and prices follow the subscriber's country (UAE for records saved
  // before countries existed), in its currency — never converted to AED.
  const { countries, failed: countriesFailed } = useEpaperCountries(visible);
  const subscriberCountry = countries
    ? findCountry(countries, user?.country || DEFAULT_COUNTRY)
    : undefined;
  const offeredPlans = subscriberCountry ? plansForCountry(plans, subscriberCountry) : [];
  const priceOf = (plan: Plan) =>
    subscriberCountry ? resolvePlanPrice(plan, subscriberCountry) : null;

  // Renew/upgrade extends from the current end date whenever the existing
  // subscription is still in the future — so the user doesn't lose any days
  // they've already paid for. Falls back to today when the subscription is
  // expired or there's no end date on record.
  const today = moment().startOf("day");
  const startDate =
    endDate && moment(endDate).startOf("day").isSameOrAfter(today)
      ? moment(endDate).startOf("day")
      : today;

  const selectedPlan = offeredPlans.find((p) => p.id === selectedPlanId) ?? null;
  const selectedPrice = selectedPlan ? priceOf(selectedPlan) : null;
  const endDateCalc = selectedPlan
    ? addPlanDuration(startDate, selectedPlan)
    : null;

  // The first plan offered in the country, unless one is already chosen.
  useEffect(() => {
    if (!offeredPlans.some((p) => p.id === selectedPlanId)) {
      setSelectedPlanId(offeredPlans[0]?.id ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans, subscriberCountry?.code]);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setPlansLoading(true);
    setPlansError(false);

    GET(API.SUBSCRIPTION_PLANS, { page: 1, pageSize: 200 })
      .then((res: any) => {
        if (cancelled) return;
        // Visibility rule (matches the "Allowed Roles" form: empty = visible
        // to everyone). Plan is shown if it has no allowed_role_ids set, OR
        // the caller's role id is in that list.
        const userRoleId =
          user?.role_id != null ? Number(user.role_id) : null;
        const rows: Plan[] = Array.isArray(res?.data)
          ? res.data
              .filter((p: Plan) => p.is_active)
              .filter((p: Plan) => {
                const allowed = Array.isArray(p.allowed_role_ids)
                  ? p.allowed_role_ids
                  : [];
                if (allowed.length === 0) return true;
                return userRoleId != null && allowed.includes(userRoleId);
              })
          : [];
        setPlans(rows);
      })
      .catch(() => {
        if (!cancelled) setPlansError(true);
      })
      .finally(() => {
        if (!cancelled) setPlansLoading(false);
      });

    setOnlineUnavailable(false);
    form.setFieldsValue({
      full_name: user?.full_name ?? "",
      email: user?.email ?? "",
      phone: user?.phone ?? "",
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!selectedPlanId || !selectedPlan) {
        message.error("Please select a subscription plan");
        return;
      }
      setSubmitting(true);

      // The server prices the plan for the subscriber's country and sets
      // the dates; only the plan goes.
      const payload = {
        full_name: values.full_name?.trim(),
        email: values.email?.trim(),
        phone: values.phone?.trim(),
        subscription_plan_id: selectedPlanId,
      };

      const response = await fetch(`${API.BASE_URL}${API.PAYTABS_CREATE}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data?.success) {
        dispatch(storePaymentData(data.tranRef));
        sessionStorage.setItem("paymentTransactionRef", data.tranRef);

        // Stash renewal context so the post-PayTabs return screen can call
        // /epaper-users/me/paytabs-renewal/complete with the user's plan +
        // dates choice. (PayTabs only carries customer name/email/phone
        // through its hosted page — the plan and date choice live here on
        // the client until we re-enter the app.)
        const renewalCtx = {
          tran_ref: data.tranRef,
          subscription_plan_id: selectedPlanId,
        };
        console.log("[RenewalModal] stashing renewal context:", renewalCtx);
        sessionStorage.setItem(
          "paytabsRenewalContext",
          JSON.stringify(renewalCtx)
        );

        window.location.href = data.paymentUrl;
      } else if (data?.error?.details?.code === "ONLINE_PAYMENT_UNAVAILABLE") {
        // PayTabs can't charge this currency: no payment, ask for an agent.
        setOnlineUnavailable(true);
        setSubmitting(false);
      } else {
        message.error(
          data?.error?.message || data?.message || "Failed to initiate payment. Please try again."
        );
        setSubmitting(false);
      }
    } catch (err: any) {
      if (err?.errorFields) return; // Ant Design form validation, not a real error
      message.error("Network error. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      width={540}
      title={
        <span style={{ fontFamily: "NotoSansMalayalam-Bold", fontSize: 17 }}>
          {modalTitle}
        </span>
      }
      destroyOnClose
    >
      {plansLoading || (!countries && !countriesFailed) ? (
        <div style={{ textAlign: "center", padding: "48px 0" }}>
          <Spin size="large" />
        </div>
      ) : plansError || countriesFailed ? (
        <Alert
          type="error"
          message="Failed to load subscription plans. Please close and try again."
          style={{ margin: "24px 0" }}
        />
      ) : offeredPlans.length === 0 ? (
        <Alert
          type="warning"
          message="No subscription plans are currently available."
          style={{ margin: "24px 0" }}
        />
      ) : (
        <div style={{ padding: "4px 0 8px" }}>
          {/* ── Situational intro ── */}
          <div
            style={{
              fontFamily: "NotoSansMalayalam-Regular",
              fontSize: 13,
              color: "#555",
              marginBottom: 14,
              lineHeight: 1.6,
            }}
          >
            {introText}
          </div>

          {/* ── Plan list ── */}
          <div
            style={{
              fontFamily: "NotoSansMalayalam-SemiBold",
              fontSize: 13,
              color: "#444",
              marginBottom: 8,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {isNew ? "Choose a Plan" : "Select Plan"}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
            {offeredPlans.map((plan) => {
              const selected = selectedPlanId === plan.id;
              const price = priceOf(plan);
              return (
                <div
                  key={plan.id}
                  onClick={() => setSelectedPlanId(plan.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    border: selected ? "2px solid #0576b7" : "1.5px solid #e0e0e0",
                    borderRadius: 10,
                    padding: "11px 16px",
                    background: selected ? "#e8f4fb" : "#fff",
                    cursor: "pointer",
                    transition: "all 0.18s",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        border: selected ? "5px solid #0576b7" : "2px solid #bbb",
                        background: "#fff",
                        flexShrink: 0,
                        transition: "border 0.15s",
                      }}
                    />
                    <div>
                      <div
                        style={{
                          fontFamily: "NotoSansMalayalam-SemiBold",
                          fontSize: 14,
                          color: "#222",
                        }}
                      >
                        {plan.plan_name}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#888",
                          fontFamily: "NotoSansMalayalam-Regular",
                        }}
                      >
                        {durationLabel(plan)}
                        {plan.region ? ` · ${plan.region}` : ""}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      fontFamily: "NotoSansMalayalam-Bold",
                      color: selected ? "#0576b7" : "#555",
                      fontSize: 15,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {price ? `${price.currency} ${price.amount}` : ""}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Read-only summary ── */}
          {selectedPlan && (
            <div
              style={{
                background: "#f5f9fe",
                border: "1px solid #d0e8f8",
                borderRadius: 10,
                padding: "12px 16px",
                marginBottom: 20,
                display: "flex",
                gap: 0,
              }}
            >
              {[
                {
                  label: "Start Date",
                  value: startDate.format("DD MMM YYYY"),
                },
                {
                  label: "End Date",
                  value: endDateCalc?.format("DD MMM YYYY") ?? "—",
                },
                {
                  label: "Amount",
                  value: selectedPrice ? `${selectedPrice.currency} ${selectedPrice.amount}` : "—",
                  highlight: true,
                },
              ].map((item, i, arr) => (
                <div
                  key={item.label}
                  style={{
                    flex: 1,
                    textAlign: "center",
                    borderRight:
                      i < arr.length - 1 ? "1px solid #d0e8f8" : "none",
                    padding: "0 8px",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: "#888",
                      marginBottom: 4,
                      textTransform: "uppercase",
                      letterSpacing: 0.3,
                    }}
                  >
                    {item.label}
                  </div>
                  <div
                    style={{
                      fontFamily: "NotoSansMalayalam-SemiBold",
                      fontSize: 13,
                      color: item.highlight ? "#0576b7" : "#222",
                    }}
                  >
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── User details ── */}
          <div
            style={{
              fontFamily: "NotoSansMalayalam-SemiBold",
              fontSize: 13,
              color: "#444",
              marginBottom: 10,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Your Details
          </div>

          <Form form={form} layout="vertical" autoComplete="off">
            <Form.Item
              label="Full Name"
              name="full_name"
              rules={[
                { required: true, message: "Please enter your full name" },
                { min: 2, message: "Minimum 2 characters" },
                {
                  validator: (_, v) =>
                    v?.trim().length >= 2
                      ? Promise.resolve()
                      : Promise.reject("Minimum 2 characters"),
                },
              ]}
            >
              <Input size="large" placeholder="Enter your full name" />
            </Form.Item>

            <Form.Item
              label="Email Address"
              name="email"
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Enter a valid email address" },
              ]}
            >
              <Input size="large" placeholder="Enter email address" />
            </Form.Item>

            <Form.Item
              label="Phone Number"
              name="phone"
              rules={[
                { required: true, message: "Please enter your phone number" },
              ]}
            >
              <Input size="large" placeholder="Enter phone number" />
            </Form.Item>
          </Form>

          {onlineUnavailable && (
            <Alert
              type="warning"
              showIcon
              message={ONLINE_PAYMENT_UNAVAILABLE_MESSAGE}
              style={{ marginBottom: 12 }}
            />
          )}

          <Button
            type="primary"
            size="large"
            block
            loading={submitting}
            onClick={handleSubmit}
            disabled={!selectedPlanId || onlineUnavailable}
            style={{
              background: "#0576b7",
              borderColor: "#0576b7",
              fontFamily: "NotoSansMalayalam-SemiBold",
              height: 48,
              fontSize: 15,
            }}
          >
            {submitLabel}
          </Button>
        </div>
      )}
    </Modal>
  );
}

export default RenewalModal;
